import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { Action, Replay, Side } from '../engine';
import { makeConfig } from '../engine';
import { setLocale, t } from '../i18n';
import { createAiClient } from '../app/aiClient';
import { MatchHost, type MatchSetup } from '../app/matchHost';
import { MatchLogger, hostSource, newMatchId, setMatchLogging } from '../app/matchLog';
import { addReplay, clearDuel, downloadJson, loadDuel, saveDuel } from '../app/saves';
import {
  aiDelayMs,
  effectiveReducedMotion,
  loadSettings,
  saveSettings,
  THEME_COLORS,
  type Settings,
} from '../app/settings';
import {
  onUpdateReleased,
  rememberForUpdate,
  takeUpdateResume,
  updateHeld,
  watchForUpdates,
} from '../app/pwa';
import { sounds } from '../app/sound';
import { APP_VERSION } from '../app/version';
import { storageIsPersistent } from '../app/storage';
import { anonymousId, telemetryFor } from '../app/telemetryStore';
import { loadTutorial, tutorialStatus } from '../app/tutorialStore';
import { loadProfile } from '../app/campaignStore';
import { runWhere } from './components/night/runText';
import { onlineStatus, roomIdFrom, roomLink, shareReplay } from '../app/online';
import type { ShareResult } from './components/ShareLink';
import { CampaignScreen } from './screens/CampaignScreen';
import { LongNightScreen } from './screens/LongNightScreen';
import { AI_NAMES, DuelSetupScreen, randomSeed } from './screens/DuelSetupScreen';
import { LeaderboardScreen } from './screens/LeaderboardScreen';
import { MatchScreen } from './screens/MatchScreen';
import { OnlineScreen } from './screens/OnlineScreen';
import { ReplaysScreen } from './screens/ReplaysScreen';
import { ReplayViewer } from './screens/ReplayViewer';
import { RoomScreen } from './screens/RoomScreen';
import { RulesScreen } from './screens/RulesScreen';
import { SeaBackground } from './components/SeaBackground';
import { ThemeContext } from './theme';
import { SpaceBackground } from './components/SpaceBackground';
import { Lamp } from './components/Lamp';
import { useSceneOnRoot } from './scene';
import { SettingsScreen } from './screens/SettingsScreen';
import { TitleScreen, type MenuTarget } from './screens/TitleScreen';
import { TutorialScreen } from './screens/TutorialScreen';

type Screen =
  | { readonly name: 'title' }
  | { readonly name: 'duelSetup' }
  | { readonly name: 'duel'; readonly host: MatchHost }
  | { readonly name: 'campaign' }
  | { readonly name: 'night' }
  | { readonly name: 'replays' }
  | {
      readonly name: 'replay';
      readonly replay: Replay;
      readonly names: Readonly<Record<Side, string>>;
    }
  | { readonly name: 'rules' }
  | { readonly name: 'settings' }
  | { readonly name: 'tutorial' }
  | { readonly name: 'online' }
  | { readonly name: 'room'; readonly id: string }
  | { readonly name: 'leaderboard' };

/** Screens that open the same way every time, so an update can reopen them. */
const REOPENABLE = [
  'duelSetup',
  'campaign',
  'night',
  'replays',
  'rules',
  'settings',
  'tutorial',
  'online',
  'leaderboard',
] as const;
const reopenable = (name: string): name is (typeof REOPENABLE)[number] =>
  (REOPENABLE as readonly string[]).includes(name);

/** A link to a room (?room=<id>) opens it straight away. */
function initialScreen(): Screen {
  const id = roomIdFrom(location.href);
  return id ? { name: 'room', id } : { name: 'title' };
}

/** A link others can open to watch a match played here. */
async function shareMatch(
  replay: Replay,
  names: Readonly<Record<Side, string>>,
): Promise<ShareResult> {
  const r = await shareReplay(replay, names);
  return 'id' in r ? { url: roomLink(r.id) } : { error: t('share.failed', { message: r.error }) };
}

/** The duel the tutorial leads into: South against the Easy computer. */
const firstDuel = (): MatchSetup => ({
  config: makeConfig({ seating: { players: ['you', 'ai'], assignment: 'fixed' } }),
  seed: randomSeed(),
  controllers: {
    you: { kind: 'human', name: t('match.you') },
    ai: { kind: 'ai', name: AI_NAMES.easy, difficulty: 'easy' },
  },
});

export function App() {
  const [settings, setSettings] = useState<Settings>(loadSettings);
  const settingsRef = useRef(settings);
  const [screen, setScreen] = useState<Screen>(initialScreen);
  // Where the player was before the app reloaded into a new release (read once).
  const [resume] = useState(takeUpdateResume);
  const [notice, setNotice] = useState<string | null>(
    storageIsPersistent() ? null : t('settings.storageUnavailable'),
  );
  const [hasSave, setHasSave] = useState(() => loadDuel() !== null);
  // Why the duel's latest save failed, until one works again (shown at the table).
  const [duelSaveError, setDuelSaveError] = useState<string | null>(null);
  // Online play and sharing need the site's server; offline or in development they are hidden.
  const [online, setOnline] = useState(false);
  useEffect(() => {
    let live = true;
    void onlineStatus().then((s) => live && setOnline(s.online));
    return () => {
      live = false;
    };
  }, []);
  // The address bar shows the room being played, so a reload (or a copied URL) comes back to it.
  useEffect(() => {
    const url = new URL(location.href);
    const want = screen.name === 'room' ? screen.id : null;
    if (url.searchParams.get('room') === want) return;
    if (want) url.searchParams.set('room', want);
    else url.searchParams.delete('room');
    history.replaceState(history.state, '', url);
  }, [screen]);
  const ai = useMemo(() => createAiClient(), []);
  const reduceMotion = effectiveReducedMotion(settings);
  useSceneOnRoot();

  useEffect(() => () => ai.dispose(), [ai]);
  useEffect(() => {
    settingsRef.current = settings;
    sounds.enabled = settings.sound;
    sounds.volume = settings.volume;
    setLocale(settings.locale);
    document.documentElement.classList.toggle('reduce-motion', reduceMotion);
    document.documentElement.classList.toggle('high-contrast', settings.highContrast);
    document.documentElement.dataset.theme = settings.theme;
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', THEME_COLORS[settings.theme]);
    setMatchLogging(settings.shareMatches);
  }, [settings, reduceMotion]);

  // A new release has taken over (src/app/pwa.ts): reload into it where the player is, as soon as
  // no turn is half made. Everything else is saved as it happens.
  const [updateReady, setUpdateReady] = useState(false);
  useEffect(() => watchForUpdates(() => setUpdateReady(true)), []);
  useEffect(() => {
    if (!updateReady) return;
    const reload = () => {
      if (updateHeld()) return;
      rememberForUpdate(screen.name);
      location.reload();
    };
    reload();
    return onUpdateReleased(reload);
  }, [updateReady, screen.name]);

  const updateSettings = (next: Settings) => {
    setSettings(next);
    const err = saveSettings(next);
    if (err) setNotice(t('error.storage', { message: err }));
  };

  const delay = useCallback(
    (action?: Action) =>
      aiDelayMs(settingsRef.current.aiSpeed, effectiveReducedMotion(settingsRef.current), action),
    [],
  );

  /**
   * Starts or resumes a duel. The duel is saved after every action and cleared when it ends; it
   * is uploaded for playtest analysis as it goes (src/app/matchLog.ts).
   */
  const openDuel = useCallback(
    (initial: MatchSetup, actions?: readonly Action[]) => {
      // A new match gets its identity here; saves made before matches had one get it now.
      const id = initial.id ?? newMatchId();
      const startedAt = initial.startedAt ?? new Date().toISOString();
      const setup: MatchSetup = { ...initial, id, startedAt };
      const telemetry = settingsRef.current.telemetry
        ? telemetryFor(setup.config, setup.seed, { S: anonymousId(), N: 'opponent' }, actions ?? [])
        : null;
      let host: MatchHost | null = null;
      let logger: MatchLogger | null = null;
      /** Saves the duel so far; says (at the table, and on the menu after) when it cannot. */
      const save = (actionsSoFar: readonly Action[]) => {
        const err = saveDuel(setup, actionsSoFar);
        const failed = err ? t('error.storage', { message: err }) : null;
        setDuelSaveError(failed);
        if (failed) setNotice(failed);
        else setHasSave(true);
      };
      host = new MatchHost(setup, {
        ai,
        delay,
        ...(actions ? { actions } : {}),
        onAction: (before, action, after, events) => {
          telemetry?.record(before, action, after, events);
          logger?.afterAction(events);
          if (after.phase === 'MATCH_OVER') {
            clearDuel();
            setHasSave(false);
            setDuelSaveError(null);
            telemetry?.finish(after);
          } else if (host) save(host.replay.actions);
        },
        onDispose: () => logger?.leave(),
      });
      logger = new MatchLogger(hostSource(host, { id, mode: 'duel', startedAt }));
      setDuelSaveError(null);
      if (!actions || !initial.id) save(host.replay.actions);
      setScreen({ name: 'duel', host });
    },
    [ai, delay],
  );

  // After an update, the screen the player was on opens again (a room's link reopens by itself;
  // a duel carries on from its save).
  useEffect(() => {
    if (!resume) return;
    const reopen = setTimeout(() => {
      if (resume.screen === 'title') setNotice(t('app.updated', { version: APP_VERSION }));
      else if (resume.screen === 'duel') {
        const save = loadDuel();
        if (save) openDuel(save.setup, save.actions);
      } else if (resume.screen === 'replay') setScreen({ name: 'replays' });
      else if (reopenable(resume.screen)) setScreen({ name: resume.screen } as Screen);
    });
    return () => clearTimeout(reopen);
  }, [resume, openDuel]);

  const leaveDuel = (host: MatchHost) => {
    host.dispose();
    setScreen({ name: 'title' });
  };

  const exportReplay = (replay: Replay, label: string) =>
    downloadJson(`pawned-${label}-${new Date().toISOString().slice(0, 10)}.json`, replay);

  // The browser's Back button (and the phone back gesture) returns to the menu instead of leaving
  // the game: one history entry stands for "inside a screen". Everything is saved as it happens,
  // so leaving a match this way loses nothing.
  // Every screen opens at its top: a page scrolled down (the duel setup's Start button, say)
  // must not leave the next screen scrolled, and the match, which fits the screen, never is.
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [screen.name]);
  useEffect(() => {
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
  }, []);

  const pushed = useRef(false);
  const ignoreNextPop = useRef(false);
  const goBack = useRef<() => void>(() => undefined);
  useEffect(() => {
    goBack.current = () => {
      if (screen.name === 'duel') leaveDuel(screen.host);
      else if (screen.name === 'replay') setScreen({ name: 'replays' });
      else setScreen({ name: 'title' });
    };
  });
  useEffect(() => {
    if (screen.name !== 'title' && !pushed.current) {
      history.pushState({ pawned: true }, '');
      pushed.current = true;
    } else if (screen.name === 'title' && pushed.current) {
      // Back on the menu through the app itself: drop the entry, so Back leaves the game as usual.
      pushed.current = false;
      ignoreNextPop.current = true;
      history.back();
    }
  }, [screen.name]);
  useEffect(() => {
    const onPop = () => {
      if (ignoreNextPop.current) {
        ignoreNextPop.current = false;
        return;
      }
      pushed.current = false;
      goBack.current();
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const onMenu = (m: MenuTarget) => {
    setNotice(null);
    switch (m) {
      case 'continue': {
        const save = loadDuel();
        if (!save) {
          setHasSave(false);
          return;
        }
        try {
          openDuel(save.setup, save.actions);
        } catch {
          clearDuel();
          setHasSave(false);
          setNotice(t('menu.resumeFailed'));
        }
        return;
      }
      case 'tutorial':
        setScreen({ name: 'tutorial' });
        return;
      case 'duel':
        setScreen({ name: 'duelSetup' });
        return;
      case 'campaign':
        setScreen({ name: 'campaign' });
        return;
      case 'night':
        setScreen({ name: 'night' });
        return;
      case 'replays':
        setScreen({ name: 'replays' });
        return;
      case 'rules':
        setScreen({ name: 'rules' });
        return;
      case 'settings':
        setScreen({ name: 'settings' });
        return;
      case 'online':
        setScreen({ name: 'online' });
        return;
      case 'leaderboard':
        setScreen({ name: 'leaderboard' });
        return;
    }
  };

  return (
    <ThemeContext.Provider value={settings.theme}>
      {settings.theme === 'sea' ? (
        <SeaBackground key="sea" still={reduceMotion} />
      ) : (
        <SpaceBackground key="void" still={reduceMotion} />
      )}
      <Lamp still={reduceMotion} />
      {renderScreen()}
    </ThemeContext.Provider>
  );

  function renderScreen() {
    switch (screen.name) {
      case 'title':
        return (
          <TitleScreen
            canContinue={hasSave}
            notice={notice}
            sharing={settings.shareMatches}
            tutorial={tutorialStatus(loadTutorial())}
            night={runWhere(loadProfile()?.ledger?.run)}
            onSelect={onMenu}
          />
        );
      case 'duelSetup':
        return (
          <DuelSetupScreen
            hasSave={hasSave}
            onBack={() => setScreen({ name: 'title' })}
            onStart={(setup) => openDuel(setup)}
          />
        );
      case 'duel': {
        const host = screen.host;
        const names = {
          S: host.getSnapshot().controllers.S.name,
          N: host.getSnapshot().controllers.N.name,
        };
        return (
          <MatchScreen
            key={host.setup.seed}
            host={host}
            settings={settings}
            reduceMotion={reduceMotion}
            exitLabel={t('menu.mainMenu')}
            saveWarning={duelSaveError}
            onExit={() => leaveDuel(host)}
            onSaveAndExit={() => leaveDuel(host)}
            onRematch={() => {
              host.dispose();
              const { config, seed, controllers } = host.setup;
              openDuel({ config, seed: `${seed}+`, controllers });
            }}
            onSaveReplay={() => {
              const snap = host.getSnapshot();
              const { error } = addReplay({
                title: `${names.S} vs ${names.N}`,
                result: snap.view.result,
                names,
                replay: host.replay,
              });
              return error ? t('error.storage', { message: error }) : null;
            }}
            onExport={() => exportReplay(host.replay, 'duel')}
            onShare={online ? () => shareMatch(host.replay, names) : undefined}
          />
        );
      }
      case 'campaign':
        return (
          <CampaignScreen
            ai={ai}
            delay={delay}
            settings={settings}
            reduceMotion={reduceMotion}
            onExit={(failedSave) => {
              if (failedSave) setNotice(failedSave);
              setScreen({ name: 'title' });
            }}
            onExportReplay={(replay) => exportReplay(replay, 'table')}
          />
        );
      case 'night':
        return (
          <LongNightScreen
            ai={ai}
            delay={delay}
            settings={settings}
            reduceMotion={reduceMotion}
            onExit={(failedSave) => {
              if (failedSave) setNotice(failedSave);
              setScreen({ name: 'title' });
            }}
            onExportReplay={(replay) => exportReplay(replay, 'night')}
          />
        );
      case 'replays':
        return (
          <ReplaysScreen
            onBack={() => setScreen({ name: 'title' })}
            onOpen={(replay, names) => setScreen({ name: 'replay', replay, names })}
            onExport={(replay) => exportReplay(replay, 'replay')}
            onShare={online ? shareMatch : undefined}
          />
        );
      case 'replay':
        return (
          <ReplayViewer
            replay={screen.replay}
            names={screen.names}
            settings={settings}
            reduceMotion={reduceMotion}
            onBack={() => setScreen({ name: 'replays' })}
          />
        );
      case 'rules':
        return (
          <RulesScreen
            onBack={() => setScreen({ name: 'title' })}
            onTutorial={() => setScreen({ name: 'tutorial' })}
          />
        );
      case 'tutorial':
        return (
          <TutorialScreen
            ai={ai}
            delay={delay}
            settings={settings}
            reduceMotion={reduceMotion}
            hasSave={hasSave}
            onExit={() => setScreen({ name: 'title' })}
            onPlayDuel={() => openDuel(firstDuel())}
            onRules={() => setScreen({ name: 'rules' })}
          />
        );
      case 'settings':
        return (
          <SettingsScreen
            settings={settings}
            onChange={updateSettings}
            onBack={() => setScreen({ name: 'title' })}
          />
        );
      case 'online':
        return (
          <OnlineScreen
            onBack={() => setScreen({ name: 'title' })}
            onOpenRoom={(id) => setScreen({ name: 'room', id })}
            onLeaderboard={() => setScreen({ name: 'leaderboard' })}
          />
        );
      case 'room':
        return (
          <RoomScreen
            key={screen.id}
            roomId={screen.id}
            settings={settings}
            reduceMotion={reduceMotion}
            onExit={() => setScreen({ name: 'online' })}
            onOpenRoom={(id) => setScreen({ name: 'room', id })}
          />
        );
      case 'leaderboard':
        return <LeaderboardScreen onBack={() => setScreen({ name: 'title' })} />;
    }
  }
}
